import React, { useState, useEffect } from "react";
import RackTabView from "./RackTabView";
import { FaEdit, FaSave } from "react-icons/fa";
import {
  useCreateItem,
  useUpdateItem,
} from "../../_helper/actions/mutation/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import { toast } from "react-toastify";
import CuSpinner from "../../common_component/component/CuSpinner";
import { useParams } from "react-router-dom";
import { useItemsFilter } from "../../_helper/actions/app/items";

const RackManagement = () => {
  // Prepopulated racks for testing
  const [racks, setRacks] = useState([]);

  const [rackInput, setRackInput] = useState(0);
  const [shelfInput, setShelfInput] = useState(0);
  const [boxInput, setBoxInput] = useState(0);
  const [namingConvention, setNamingConvention] = useState("numeric");
  const [isEditMode, setIsEditMode] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [rackCounter, setRackCounter] = useState(0); // Dynamically calculated
  const [searchQuery, setSearchQuery] = useState(""); // For search functionality

  const { id } = useParams("");

  // Dynamically calculate the rackCounter based on existing data
  useEffect(() => {
    const initialRackCounter = racks.length;
    setRackCounter(initialRackCounter);
  }, [racks]);

  const { data, isLoading, isFetched } = useItemsFilter(
    ApiEndPoint.PHARMACY,
    `?action=getRacks&pharmacy_id=${id}&all_rack_pharmacy=True
`
  );

  const mutation = useCreateItem(ApiEndPoint.PHARMACY);

  useEffect(() => {
    if (isFetched) {
      setRacks(data);
    }
  }, [isFetched]);

  console.log("Racks", data);

  const generateName = (index, type) => {
    if (namingConvention === "alphabetic") {
      let name = "";
      let i = index;
      while (i >= 0) {
        name = String.fromCharCode((i % 26) + 65) + name;
        i = Math.floor(i / 26) - 1;
      }
      return `${type} ${name}`;
    }
    return `${type} ${index + 1}`;
  };

  const filteredRacks = racks
    .map((rack) => {
      if (!searchQuery.trim()) return rack;

      const query = searchQuery.toLowerCase();

      // Filter shelves and boxes within the rack
      const filteredShelves = rack.shelves
        .map((shelf) => {
          const filteredBoxes = shelf.boxes.filter(
            (box) =>
              box.box_name.toLowerCase().includes(query) ||
              box.item.toLowerCase().includes(query)
          );

          if (
            filteredBoxes.length > 0 ||
            shelf.name.toLowerCase().includes(query) ||
            shelf.shelf_category.toLowerCase().includes(query)
          ) {
            return { ...shelf, boxes: filteredBoxes };
          }
          return null;
        })
        .filter(Boolean); // Remove null values

      if (
        filteredShelves.length > 0 ||
        rack.rack_name.toLowerCase().includes(query) ||
        rack.category.toLowerCase().includes(query)
      ) {
        return { ...rack, shelves: filteredShelves };
      }
      return null;
    })
    .filter(Boolean); // Remove null values

  const generateStructure = () => {
    if (rackInput <= 0 || shelfInput <= 0 || boxInput <= 0) {
      alert("Please enter valid numbers for racks, shelves, and boxes.");
      return;
    }

    setIsGenerating(true);

    const startingRack = rackCounter;
    const totalRacksToGenerate = rackInput;

    const newRacks = Array.from(
      { length: totalRacksToGenerate },
      (_, rackIndex) => ({
        rack_name: generateName(startingRack + rackIndex, "Rack"),
        category: `Category ${startingRack + rackIndex + 1}`,
        pharmacy_id: Number(id),
        shelves: Array.from({ length: shelfInput }, (_, shelfIndex) => ({
          shelf_name: generateName(shelfIndex, "Shelf"),
          shelf_category: `Subcategory ${shelfIndex + 1}`,
          boxes: Array.from({ length: boxInput }, (_, boxIndex) => ({
            box_name: generateName(boxIndex, "Box"),
            item: [],
          })),
        })),
      })
    );

    // console.log("New Rack", newRacks);

    const payload = {
      action: "postRacks",
      racks: newRacks,
      generate_many: true,
    };

    mutation.mutate(payload, {
      onSuccess: () => {
        setRacks((prevRacks) => [...prevRacks, ...newRacks]);
        setRackCounter(startingRack + totalRacksToGenerate);
        setIsGenerating(false);
        toast.success("Structure Saved!");
      },
    });
  };

  const addRack = () => {
    const newRack = {
      rack_name: generateName(rackCounter, "Rack"),
      pharmacy_id: id,
      category: `Category ${rackCounter + 1}`,
      shelves: [],
    };
    setRacks((prevRacks) => [...prevRacks, newRack]);
    setRackCounter(rackCounter + 1);
  };

  const post_mutation = useCreateItem(ApiEndPoint.PHARMACY);
  const update_mutation = useUpdateItem(ApiEndPoint.PHARMACY);

  const saveRackStructure = () => {
    // Create a deep copy of the racks to avoid mutating the original state
    const cleanedRacks = JSON.parse(JSON.stringify(racks));

    // Iterate through the racks, shelves, and boxes to remove the `batches` field from items
    cleanedRacks.forEach((rack) => {
      rack.shelves.forEach((shelf) => {
        shelf.boxes.forEach((box) => {
          if (box.items && box.items.length > 0) {
            box.items = box.items.map((item) => {
              // Remove the `batches` field from each item
              const { batches, ...rest } = item;
              return rest;
            });
          }
        });
      });
    });

    // Prepare the payload
    const payload = {
      action: "patchPharmacy",
      racks: cleanedRacks, // Use the cleaned racks
      id: id,
    };

    // Send the payload to the server
    update_mutation.mutate(
      { data: { ...payload } },
      {
        onSuccess: () => {
          setIsEditMode(false);
          toast.success("Rack structure saved successfully!");
        },
      }
    );
  };
  return (
    <div className="bg-white p-6 rounded shadow">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">
        Pharmacy Rack Management
      </h2>

      {/* Search Bar */}
      <div className="mb-6">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search racks, shelves, or boxes..."
          className="border border-gray-300 rounded px-4 py-2 w-full"
        />
      </div>

      {!racks.length && (
        <div className="mb-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Naming Convention
            </label>
            <select
              value={namingConvention}
              onChange={(e) => setNamingConvention(e.target.value)}
              className="border border-gray-300 rounded px-4 py-2 w-full"
            >
              <option value="numeric">Numeric</option>
              <option value="alphabetic">Alphabetic</option>
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Number of Racks
              </label>
              <input
                type="number"
                value={rackInput}
                onChange={(e) => setRackInput(Number(e.target.value))}
                min="0"
                className="border border-gray-300 rounded px-4 py-2 w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Number of Shelves per Rack
              </label>
              <input
                type="number"
                value={shelfInput}
                onChange={(e) => setShelfInput(Number(e.target.value))}
                min="0"
                className="border border-gray-300 rounded px-4 py-2 w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Number of Boxes per Shelf
              </label>
              <input
                type="number"
                value={boxInput}
                onChange={(e) => setBoxInput(Number(e.target.value))}
                min="0"
                className="border border-gray-300 rounded px-4 py-2 w-full"
              />
            </div>
          </div>
          <button
            className="mt-4 bg-blue-500 text-white px-6 py-2 rounded shadow hover:bg-blue-600"
            onClick={generateStructure}
            disabled={isGenerating}
          >
            {isGenerating ? "Generating..." : "Generate Structure"}
          </button>
        </div>
      )}

      {filteredRacks.length > 0 && (
        <div>
          <div className="mb-6 flex justify-end space-x-4">
            {isEditMode ? (
              <button
                className="bg-green-500 text-white px-6 py-2 rounded shadow hover:bg-green-600"
                onClick={saveRackStructure}
              >
                {post_mutation.isLoading ? (
                  <CuSpinner />
                ) : (
                  <>
                    <FaSave className="inline mr-2" />
                    Save
                  </>
                )}
              </button>
            ) : (
              <button
                className="bg-blue-500 text-white px-6 py-2 rounded shadow hover:bg-blue-600"
                onClick={() => setIsEditMode(true)}
              >
                <FaEdit className="inline mr-2" />
                Edit
              </button>
            )}
          </div>
          <RackTabView
            racks={filteredRacks} // Pass filtered racks based on search query
            setRacks={setRacks}
            isEditMode={isEditMode}
            generateName={generateName}
            addRack={addRack}
            pharmacy_id={id}
          />
        </div>
      )}
    </div>
  );
};

export default RackManagement;
